<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import mapBackground from '../assets/design/backgrounds/map-background.png'
import { getTasteRelations, getWordStats, isMockMode } from '../services/experience-service'
import { useExperienceStore } from '../stores/experience'
import type { RelationStat, WordStat } from '../types/questionnaire'

interface BubbleSlot {
  x: number
  y: number
  baseSize: number
}

interface DisplayBubble extends WordStat, BubbleSlot {
  size: number
  color: string
}

interface RelationLine extends RelationStat {
  path: string
  strokeWidth: number
  opacity: number
}

const tasteSlots: BubbleSlot[] = [
  { x: 9, y: 26, baseSize: 12.5 },
  { x: 23, y: 19, baseSize: 10.5 },
  { x: 38, y: 26, baseSize: 11.5 },
  { x: 16, y: 48, baseSize: 9.5 },
  { x: 32, y: 48, baseSize: 13 },
  { x: 45, y: 47, baseSize: 9 },
  { x: 8, y: 67, baseSize: 8.5 },
  { x: 22, y: 69, baseSize: 10 },
  { x: 39, y: 68, baseSize: 8.5 },
  { x: 49, y: 67, baseSize: 7.5 },
]

const memorySlots: BubbleSlot[] = [
  { x: 61, y: 22, baseSize: 11.5 },
  { x: 76, y: 20, baseSize: 13 },
  { x: 91, y: 28, baseSize: 9.5 },
  { x: 56, y: 45, baseSize: 8.5 },
  { x: 70, y: 46, baseSize: 10.5 },
  { x: 85, y: 48, baseSize: 12 },
  { x: 96, y: 52, baseSize: 7.5 },
  { x: 61, y: 67, baseSize: 9 },
  { x: 77, y: 69, baseSize: 10.5 },
  { x: 91, y: 69, baseSize: 8.5 },
]

const colors = [
  '#ff9e87',
  '#f48ba1',
  '#ffd358',
  '#cdd275',
  '#abc5e7',
  '#9bb49c',
  '#e9bdd0',
  '#b3c1e6',
  '#a9d7d9',
  '#edc58d',
]

const router = useRouter()
const experienceStore = useExperienceStore()
const tasteStats = ref<WordStat[]>([])
const memoryStats = ref<WordStat[]>([])
const loading = ref(true)
const errorMessage = ref('')
const selectedTasteCode = ref('')
const relations = ref<RelationStat[]>([])
const relationLoading = ref(false)
const relationError = ref('')
let relationRequestId = 0

function createBubbles(stats: WordStat[], slots: BubbleSlot[], offset: number): DisplayBubble[] {
  const source = [...stats]
    .filter((item) => item.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, slots.length)

  if (source.length === 0) {
    return []
  }

  const maxCount = Math.max(...source.map((item) => item.count))

  return source.map((item, index) => {
    const slot = slots[index]
    const importance = Math.sqrt(item.count / Math.max(1, maxCount))

    return {
      ...item,
      ...slot,
      size: slot.baseSize * (0.78 + importance * 0.26),
      color: colors[(index + offset) % colors.length],
    }
  })
}

const tasteBubbles = computed(() => createBubbles(tasteStats.value, tasteSlots, 0))
const memoryBubbles = computed(() => createBubbles(memoryStats.value, memorySlots, 4))
const hasMapData = computed(() => tasteBubbles.value.length > 0 || memoryBubbles.value.length > 0)
const relatedMemoryCodes = computed(() => new Set(relations.value.map((item) => item.targetCode)))
const selectedTaste = computed(() => (
  tasteBubbles.value.find((item) => item.code === selectedTasteCode.value) || null
))
const mapDescription = computed(() => {
  if (!selectedTaste.value) {
    return '不同味道串起了大家不同的记忆与感受'
  }
  if (relationLoading.value) {
    return `正在寻找“${selectedTaste.value.label}”连接的记忆…`
  }
  if (relationError.value) {
    return relationError.value
  }
  if (relations.value.length === 0) {
    return `“${selectedTaste.value.label}”还没有可展示的记忆关联`
  }

  const summary = relations.value
    .map((item) => `${item.targetLabel} ${item.count} 次`)
    .join('、')
  return `“${selectedTaste.value.label}”最常连接：${summary}`
})
const relationLines = computed<RelationLine[]>(() => {
  if (!selectedTaste.value) {
    return []
  }

  const maxCount = Math.max(1, ...relations.value.map((item) => item.count))
  return relations.value.flatMap((relation) => {
    const target = memoryBubbles.value.find((item) => item.code === relation.targetCode)
    if (!target) {
      return []
    }

    const startX = selectedTaste.value?.x || 0
    const startY = selectedTaste.value?.y || 0
    const controlOffset = Math.max(10, (target.x - startX) * 0.42)
    const strength = Math.sqrt(relation.count / maxCount)
    return [{
      ...relation,
      path: `M ${startX} ${startY} C ${startX + controlOffset} ${startY}, ${target.x - controlOffset} ${target.y}, ${target.x} ${target.y}`,
      strokeWidth: 3 + strength * 7,
      opacity: 0.42 + strength * 0.4,
    }]
  })
})

onMounted(async () => {
  try {
    const [taste, memory] = await Promise.all([
      getWordStats('taste'),
      getWordStats('memory_category'),
    ])
    tasteStats.value = taste
    memoryStats.value = memory
  } catch {
    errorMessage.value = experienceStore.lastSubmissionQueued
      ? '本次回答已离线保存，恢复网络后将自动提交并更新地图'
      : '味觉地图暂时加载失败'
  } finally {
    loading.value = false
  }
})

function returnHome() {
  experienceStore.resetSession()
  void router.replace({ name: 'home' })
}

function clearRelations() {
  relationRequestId += 1
  selectedTasteCode.value = ''
  relations.value = []
  relationError.value = ''
  relationLoading.value = false
}

async function selectTaste(bubble: DisplayBubble) {
  if (selectedTasteCode.value === bubble.code) {
    clearRelations()
    return
  }

  const requestId = relationRequestId + 1
  relationRequestId = requestId
  selectedTasteCode.value = bubble.code
  relations.value = []
  relationError.value = ''
  relationLoading.value = true

  try {
    const result = await getTasteRelations(bubble.code, 5)
    if (requestId === relationRequestId) {
      relations.value = result
    }
  } catch {
    if (requestId === relationRequestId) {
      relationError.value = '关联数据暂时加载失败，请稍后再试'
    }
  } finally {
    if (requestId === relationRequestId) {
      relationLoading.value = false
    }
  }
}
</script>

<template>
  <div class="view map-view" :style="{ backgroundImage: `url(${mapBackground})` }">
    <p v-if="isMockMode" class="map-data-badge">演示数据</p>

    <header class="map-heading">
      <h1>大家的味觉地图</h1>
      <p>{{ mapDescription }}</p>
    </header>

    <div v-if="loading" class="map-status" aria-live="polite">
      正在汇聚大家的味觉与记忆…
    </div>
    <div v-else-if="errorMessage" class="map-status map-status--error" aria-live="polite">
      {{ errorMessage }}
    </div>
    <div v-else-if="hasMapData" class="bubble-map" aria-live="polite" @click.self="clearRelations">
      <p class="bubble-column-label bubble-column-label--taste">味道</p>
      <p class="bubble-column-label bubble-column-label--memory">记忆</p>
      <svg
        v-if="relationLines.length > 0"
        class="relation-lines"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          v-for="line in relationLines"
          :key="`${line.sourceCode}-${line.targetCode}`"
          class="relation-line"
          :d="line.path"
          :stroke-width="line.strokeWidth"
          :opacity="line.opacity"
        />
      </svg>
      <button
        v-for="bubble in tasteBubbles"
        :key="`taste-${bubble.code}`"
        class="map-bubble map-bubble--taste"
        :class="{
          'map-bubble--highlighted': bubble.code === experienceStore.lastSubmittedTasteCode,
          'map-bubble--selected': bubble.code === selectedTasteCode,
          'map-bubble--dimmed': selectedTasteCode && bubble.code !== selectedTasteCode,
        }"
        :style="{
          left: `${bubble.x}%`,
          top: `${bubble.y}%`,
          width: `${bubble.size}%`,
          paddingTop: `${bubble.size}%`,
          backgroundColor: bubble.color,
        }"
        type="button"
        :aria-pressed="bubble.code === selectedTasteCode"
        :title="`${bubble.label}：${bubble.count} 次`"
        @click="selectTaste(bubble)"
      >
        <span class="map-bubble-inner">
          <strong>{{ bubble.label }}</strong>
          <small>{{ bubble.count }} 次</small>
        </span>
      </button>
      <button
        v-for="bubble in memoryBubbles"
        :key="`memory-${bubble.code}`"
        class="map-bubble map-bubble--memory"
        :class="{
          'map-bubble--highlighted': bubble.code === experienceStore.lastSubmittedMemoryCode,
          'map-bubble--related': relatedMemoryCodes.has(bubble.code),
          'map-bubble--dimmed': selectedTasteCode && !relatedMemoryCodes.has(bubble.code),
        }"
        :style="{
          left: `${bubble.x}%`,
          top: `${bubble.y}%`,
          width: `${bubble.size}%`,
          paddingTop: `${bubble.size}%`,
          backgroundColor: bubble.color,
        }"
        type="button"
        :title="`${bubble.label}：${bubble.count} 次`"
      >
        <span class="map-bubble-inner">
          <strong>{{ bubble.label }}</strong>
          <small>{{ bubble.count }} 次</small>
        </span>
      </button>
    </div>
    <div v-else class="map-status" aria-live="polite">
      还没有真实答卷，完成一次体验后这里会出现统计
    </div>

    <p
      v-if="experienceStore.lastSubmissionQueued && !errorMessage"
      class="map-sync-notice"
      aria-live="polite"
    >
      本次回答已离线保存，恢复网络后将自动提交
    </p>

    <button class="map-return" type="button" @click="returnHome">
      返回首页
    </button>
  </div>
</template>
